import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getData } from '../../../services/apiService';

// SVG Icon Components (keep these as is)
const EventIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const ParticipantIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const RevenueIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 8h6m-6 4h6m-2.5 4H9m0 0l2.5-4M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z" />
  </svg>
);

const UpcomingIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const CompletedIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const OngoingIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

// Circular Progress Component for Payment Analytics (keep as is)
const CircularProgressChart = ({ successRate, failed, pending }) => {
  const radius = 70;
  const circumference = 2 * Math.PI * radius;

  const successDash = (successRate / 100) * circumference;
  const failedDash = (failed / 100) * circumference;
  const pendingDash = (pending / 100) * circumference;

  return (
    <div className="relative w-40 h-40">
      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 160 160">
        {/* Success segment - always visible if > 0 */}
        {successRate > 0 && (
          <circle
            cx="80"
            cy="80"
            r={radius}
            stroke="url(#successGradient)"
            strokeWidth="12"
            fill="none"
            strokeDasharray={`${successDash} ${circumference}`}
            strokeLinecap="round"
          />
        )}

        {/* Failed segment - only visible if > 0 */}
        {failed > 0 && (
          <circle
            cx="80"
            cy="80"
            r={radius}
            stroke="url(#failedGradient)"
            strokeWidth="12"
            fill="none"
            strokeDasharray={`${failedDash} ${circumference}`}
            strokeDashoffset={-successDash}
            strokeLinecap="round"
          />
        )}

        {/* Pending segment - only visible if > 0 */}
        {pending > 0 && (
          <circle
            cx="80"
            cy="80"
            r={radius}
            stroke="url(#pendingGradient)"
            strokeWidth="12"
            fill="none"
            strokeDasharray={`${pendingDash} ${circumference}`}
            strokeDashoffset={-(successDash + failedDash)}
            strokeLinecap="round"
          />
        )}

        {/* Background circle for when all values are 0 or to show empty state */}
        {(successRate === 0 && failed === 0 && pending === 0) && (
          <circle
            cx="80"
            cy="80"
            r={radius}
            stroke="#E5E7EB"  // Gray color for empty state
            strokeWidth="12"
            fill="none"
            strokeDasharray={`${circumference} ${circumference}`}
            strokeLinecap="round"
          />
        )}

        <defs>
          <linearGradient id="successGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#10B981" />
            <stop offset="100%" stopColor="#34D399" />
          </linearGradient>
          <linearGradient id="failedGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#EF4444" />
            <stop offset="100%" stopColor="#F87171" />
          </linearGradient>
          <linearGradient id="pendingGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#F59E0B" />
            <stop offset="100%" stopColor="#FBBF24" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-gray-900">{successRate}%</span>
        <span className="text-sm text-gray-600">Success</span>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [timeRange, setTimeRange] = useState('SixMonths'); // Changed default to match API
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await getData('Dashboard/dashboard');

        if (response.IsError === false) {
          setDashboardData(response.Result);
        } else {
          throw new Error(response.Message || 'Failed to fetch dashboard data');
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(
          err.response?.data?.message ||
          err.message ||
          'Failed to load dashboard data. Please try again later.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Helper function to calculate growth rate between current and previous period
  const calculateGrowthRate = (currentData, previousData) => {
    if (!currentData || !previousData || previousData.length === 0) return 0;
    
    const currentTotal = currentData.reduce((sum, item) => sum + (item.Registrations || 0), 0);
    const previousTotal = previousData.reduce((sum, item) => sum + (item.Registrations || 0), 0);
    
    if (previousTotal === 0) return 0;
    
    const growthRate = ((currentTotal - previousTotal) / previousTotal) * 100;
    return Math.round(growthRate * 10) / 10; // Round to 1 decimal place
  };

  // Helper function to get data for selected time range
  const getTimeRangeData = () => {
    if (!dashboardData?.RegistrationTrends?.TimeRanges) return null;

    const { TimeRanges } = dashboardData.RegistrationTrends;
    
    // Map the timeRange to the correct API property
    switch(timeRange) {
      case 'Days':
        return {
          current: TimeRanges.Days || [],
          previous: [] // API doesn't provide previous period data
        };
      case 'Weeks':
        return {
          current: TimeRanges.Weeks || [],
          previous: []
        };
      case 'SixMonths':
        return {
          current: TimeRanges.SixMonths || [],
          previous: []
        };
      case 'OneYear':
        return {
          current: TimeRanges.OneYear || [],
          previous: []
        };
      default:
        return {
          current: TimeRanges.SixMonths || [],
          previous: []
        };
    }
  };

  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
  };

  // Helper function to clean and format values
  const cleanValue = (value, statId) => {
    if (typeof value !== 'string') return value;

    if (statId === 'revenue') {
      // Remove any non-numeric characters except numbers, dots, and commas
      const cleaned = value.replace(/[^0-9.,-]/g, '');
      
      // Try to parse as number
      const numericValue = parseFloat(cleaned.replace(/,/g, ''));
      
      if (!isNaN(numericValue)) {
        return formatCurrency(numericValue);
      }
      
      // If cleaning fails, return the original with currency symbol
      return formatCurrency(0);
    }

    return value;
  };

  const getStatConfig = (statId) => {
    const configs = {
      'total-events': {
        icon: EventIcon,
        gradient: 'from-purple-500 to-purple-600',
        textColor: 'text-purple-600',
        bgColor: 'bg-purple-50'
      },
      'participants': {
        icon: ParticipantIcon,
        gradient: 'from-blue-500 to-blue-600',
        textColor: 'text-blue-600',
        bgColor: 'bg-blue-50'
      },
      'revenue': {
        icon: RevenueIcon,
        gradient: 'from-green-500 to-green-600',
        textColor: 'text-green-600',
        bgColor: 'bg-green-50'
      }
    };
    return configs[statId] || configs['total-events'];
  };

  const getStatusConfig = (status) => {
    const configs = {
      'upcoming': {
        icon: UpcomingIcon,
        bgColor: 'bg-blue-50',
        textColor: 'text-blue-800',
        countBg: 'bg-blue-100',
        iconColor: 'text-blue-600'
      },
      'completed': {
        icon: CompletedIcon,
        bgColor: 'bg-green-50',
        textColor: 'text-green-800',
        countBg: 'bg-green-100',
        iconColor: 'text-green-600'
      },
      'ongoing': {
        icon: OngoingIcon,
        bgColor: 'bg-purple-50',
        textColor: 'text-purple-800',
        countBg: 'bg-purple-100',
        iconColor: 'text-purple-600'
      },
      'cancelled': {
        icon: CompletedIcon,
        bgColor: 'bg-red-50',
        textColor: 'text-red-800',
        countBg: 'bg-red-100',
        iconColor: 'text-red-600'
      },
      'unknown': {
        icon: UpcomingIcon,
        bgColor: 'bg-gray-50',
        textColor: 'text-gray-800',
        countBg: 'bg-gray-100',
        iconColor: 'text-gray-600'
      }
    };
    return configs[status] || configs['upcoming'];
  };

  const getEventStatusConfig = (status) => {
    const configs = {
      'completed': {
        bg: 'bg-green-100',
        text: 'text-green-800'
      },
      'upcoming': {
        bg: 'bg-blue-100',
        text: 'text-blue-800'
      },
      'ongoing': {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800'
      },
      'cancelled': {
        bg: 'bg-red-100',
        text: 'text-red-800'
      },
      'unknown': {
        bg: 'bg-gray-100',
        text: 'text-gray-800'
      }
    };
    return configs[status] || configs['upcoming'];
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date not set';
    
    // Handle invalid dates like "1900-01-01"
    const date = new Date(dateString);
    if (isNaN(date.getTime()) || date.getFullYear() <= 1900) {
      return 'Date not set';
    }
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50/30 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50/30 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-4">Error loading dashboard</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gray-50/30 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">No data available</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors mt-4"
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  // Destructure with correct property names from the API response
  const {
    Statistics = [],
    RegistrationTrends = {},
    PaymentAnalytics = {},
    EventStatus = {},
    RecentEvents = []
  } = dashboardData;

  // Use the Statistics array directly from the API and update the title for participants
  const statistics = Statistics.map(stat => {
    if (stat.id === 'participants') {
      return {
        ...stat,
        title: 'Registered'
      };
    }
    if (stat.id === 'revenue') {
      return {
        ...stat,
        title: 'Collected Amount'
      };
    }
    return stat;
  }).filter(stat => stat.id !== 'avg-ratings');

  // Get current and previous data based on selected time range
  const timeRangeData = getTimeRangeData();
  const currentData = timeRangeData?.current || [];
  const previousData = timeRangeData?.previous || [];
  
  // Calculate growth rate
  const growthRate = calculateGrowthRate(currentData, previousData) || RegistrationTrends.GrowthRate || 0;
  
  // Calculate total registrations for current period
  const totalRegistrations = currentData.reduce((sum, item) => sum + (item.Registrations || 0), 0) || 
                             RegistrationTrends.TotalRegistrations || 0;
  
  // Calculate max registrations for chart scaling
  const maxRegistrations = currentData.length > 0 
    ? Math.max(...currentData.map(item => item.Registrations || 0)) 
    : 0;

  // Get display label for current time range
  const getTimeRangeLabel = () => {
    switch(timeRange) {
      case 'Days': return 'Daily';
      case 'Weeks': return 'Weekly';
      case 'SixMonths': return 'Last 6 Months';
      case 'OneYear': return 'Last 1 Year';
      default: return 'Last 6 Months';
    }
  };

  // Get the label key based on time range
  const getLabelKey = () => {
    switch(timeRange) {
      case 'Days': return 'Day';
      case 'Weeks': return 'Week';
      case 'SixMonths': 
      case 'OneYear': return 'Month';
      default: return 'Month';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/30 p-6">
      {/* Stats Grid - 3 columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {statistics.map((stat) => {
          const config = getStatConfig(stat.id);
          const IconComponent = config.icon;

          // Clean the value before displaying
          const displayValue = cleanValue(stat.value, stat.id);

          return (
            <div key={stat.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-2">{stat.title}</p>
                  <p className="text-3xl font-bold text-gray-900">{displayValue}</p>
                  {stat.trend && stat.trend.value > 0 && stat.trend.type && (
                    <p className={`text-xs mt-1 ${stat.trend.type === 'increase' ? 'text-green-500' : 'text-red-500'}`}>
                      {stat.trend.type === 'increase' ? '↑' : '↓'} {stat.trend.value}%
                    </p>
                  )}
                </div>
                <div className={`p-3 rounded-xl ${config.bgColor}`}>
                  <div className={config.textColor}>
                    <IconComponent />
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <div className={`w-full bg-gradient-to-r ${config.gradient} h-2 rounded-full`}></div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 mb-8">
        {/* Enhanced Registration Trend */}
        <div className="xl:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Registration Trend</h3>
              <p className="text-gray-600 text-sm">
                {getTimeRangeLabel()}: {totalRegistrations.toLocaleString()} registrations
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'Days', label: 'Day' },
                { value: 'Weeks', label: 'Week' },
                { value: 'SixMonths', label: '6 Months' },
                { value: 'OneYear', label: '1 Year' }
              ].map((range) => (
                <button
                  key={range.value}
                  onClick={() => handleTimeRangeChange(range.value)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                    timeRange === range.value
                      ? 'bg-blue-500 text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>

          {/* Enhanced Bar Chart */}
          <div className="h-64 relative">
            <div className="absolute inset-0 flex items-end justify-between px-2 pb-8">
              {currentData.length > 0 ? (
                currentData.map((item, index) => {
                  const registrations = item.Registrations || 0;
                  const height = maxRegistrations > 0 ? (registrations / maxRegistrations) * 140 : 0;
                  const barWidth = timeRange === 'OneYear' || timeRange === 'SixMonths' ? 'w-8' : 'w-12';
                  
                  // Get the label based on the time range
                  const label = item.Day || item.Week || item.Month || '';

                  return (
                    <div key={index} className="flex flex-col items-center group relative">
                      {/* Registration count */}
                      <div className="mb-2 text-xs font-medium text-gray-600">
                        {registrations.toLocaleString()}
                      </div>

                      {/* Bar */}
                      <div
                        className={`${barWidth} bg-gradient-to-t from-blue-500 to-blue-400 rounded-t-lg transition-all duration-500 hover:from-blue-600 hover:to-blue-500 hover:shadow-lg relative group`}
                        style={{ height: `${height}px` }}
                      >
                        {/* Hover tooltip */}
                        <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs py-2 px-3 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap shadow-xl">
                          <div className="font-semibold">{registrations.toLocaleString()}</div>
                          <div className="text-gray-300">registrations</div>
                          <div className="text-gray-300">{formatCurrency(item.Revenue || 0)} revenue</div>
                        </div>
                      </div>

                      {/* Month/Day label */}
                      <div className="mt-3 text-sm font-medium text-gray-600">{label}</div>
                    </div>
                  );
                })
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-500">
                  No registration data available for this period
                </div>
              )}
            </div>

            {/* Grid lines */}
            <div className="absolute inset-0 flex flex-col justify-between">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="border-t border-gray-100"></div>
              ))}
            </div>
          </div>

          {/* Additional metrics */}
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-blue-600 font-medium">Growth Rate</div>
              <div className="text-2xl font-bold text-blue-800">
                {growthRate > 0 ? '+' : ''}{growthRate}%
              </div>
              <div className="text-xs text-blue-600 mt-1">
                vs previous period
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm text-green-600 font-medium">Collected Amount</div>
              <div className="text-2xl font-bold text-green-800">
                {formatCurrency(currentData.reduce((sum, item) => sum + (item.Revenue || 0), 0))}
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Payment Analytics with Circular Graph */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-900">Payment Analytics</h3>
            <p className="text-gray-600 text-sm">Transaction success rate</p>
          </div>

          {/* Circular Progress Chart */}
          <div className="flex flex-col items-center z-10 justify-center mb-6">
            <CircularProgressChart
              successRate={PaymentAnalytics.successRate || 0}
              failed={PaymentAnalytics.failed || 0}
              pending={PaymentAnalytics.pending || 0}
            />
          </div>

          {/* Payment breakdown */}
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-gradient-to-r from-green-500 to-green-400 rounded-full"></div>
                <span className="text-sm font-medium text-green-800">Successful</span>
              </div>
              <span className="text-sm font-bold text-green-800">{PaymentAnalytics.successRate || 0}%</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-gradient-to-r from-red-500 to-red-400 rounded-full"></div>
                <span className="text-sm font-medium text-red-800">Failed</span>
              </div>
              <span className="text-sm font-bold text-red-800">{PaymentAnalytics.failed || 0}%</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-gradient-to-r from-yellow-500 to-yellow-400 rounded-full"></div>
                <span className="text-sm font-medium text-yellow-800">Pending</span>
              </div>
              <span className="text-sm font-bold text-yellow-800">{PaymentAnalytics.pending || 0}%</span>
            </div>
          </div>
          
          {/* Total Transactions */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Transactions</span>
              <span className="text-lg font-bold text-gray-900">{PaymentAnalytics.totalTransactions || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Events Section */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mb-8">
        {/* Event Stats */}
        <div className="lg:col-span-1 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Event Status</h3>
          <div className="space-y-4">
            {EventStatus && Object.entries(EventStatus).map(([status, count]) => {
              const config = getStatusConfig(status);
              const IconComponent = config.icon;

              return (
                <div key={status} className={`flex items-center justify-between p-4 ${config.bgColor} rounded-xl`}>
                  <div>
                    <p className={`text-sm font-medium ${config.textColor} capitalize`}>{status}</p>
                    <p className={`text-2xl font-bold ${config.textColor}`}>{count}</p>
                  </div>
                  <div className={`p-3 ${config.countBg} rounded-lg ${config.iconColor}`}>
                    <IconComponent />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Events Table with Scroll - Removed Rating column */}
        <div className="lg:col-span-3 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Recent & Upcoming Events</h3>
              <p className="text-gray-600 text-sm">All your event activities</p>
            </div>
            <Link to="/events" className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium">
              View All Events
            </Link>
          </div>

          {/* Scrollable Table Container */}
          <div className="overflow-x-auto">
            <div className="min-w-full">
              <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                <table className="w-full">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left py-4 px-4 text-sm font-semibold text-gray-700 sticky top-0 bg-gray-50">Event Name</th>
                      <th className="text-left py-4 px-4 text-sm font-semibold text-gray-700 sticky top-0 bg-gray-50">Date</th>
                      <th className="text-left py-4 px-4 text-sm font-semibold text-gray-700 sticky top-0 bg-gray-50">Registered</th>
                      <th className="text-left py-4 px-4 text-sm font-semibold text-gray-700 sticky top-0 bg-gray-50">Collected Amount</th>
                      <th className="text-left py-4 px-4 text-sm font-semibold text-gray-700 sticky top-0 bg-gray-50">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {RecentEvents.length > 0 ? (
                      RecentEvents.map((event) => {
                        const statusConfig = getEventStatusConfig(event.status);

                        return (
                          <tr key={event.id} className="hover:bg-gray-50/50 transition-colors">
                            <td className="py-4 px-4">
                              {event.name}
                              <div className="text-xs text-gray-500 mt-1">
                                {event.category || 'No category'} - {event.location || 'No location'}
                              </div>
                            </td>
                            <td className="py-4 px-4 text-sm text-gray-600 whitespace-nowrap">{formatDate(event.date)}</td>
                            <td className="py-4 px-4 text-sm text-gray-600 text-center">{event.participants?.toLocaleString() || 0}</td>
                            <td className="py-4 px-4 text-sm font-medium text-gray-700 whitespace-nowrap">{formatCurrency(event.revenue)}</td>
                            <td className="py-4 px-4">
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${statusConfig.bg} ${statusConfig.text}`}>
                                {event.status || 'unknown'}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" className="py-8 text-center text-gray-500">
                          No recent events found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Summary footer */}
          <div className="mt-4 flex justify-between items-center text-sm text-gray-600">
            <span>Showing {RecentEvents.length} events</span>
            <span>Last updated: {new Date().toLocaleDateString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;